"use client";

import { useRef, useState } from "react";
import { ReactionPillStack, ReactionTooltipCard } from "./ReactionPills";
import { usePostReactors } from "@/lib/hooks/usePostReaction";
import { useSignedIn } from "@/lib/hooks/useSignedIn";
import { buildReactionTooltip } from "@/lib/reactions";
import type { ReactionSummary as Summary } from "@theta/api-client";

export function ReactionSummary({
  counts,
  postId,
}: {
  counts: Summary["counts"];
  postId?: number;
}) {
  const [visible, setVisible] = useState(false);
  /** The chip stack, which the tooltip is positioned against. */
  const anchorRef = useRef<HTMLDivElement>(null);
  /**
   * WHO reacted, read on hover and cached by react-query — which is what makes
   * the viewer's OWN reaction show up in it (OSO-449).
   *
   * This used to be a `useState` filled by a one-shot fetch behind a
   * `fetchedRef` that was never lowered, so the list was whatever it had been
   * the first time the tooltip opened. `usePostReaction` drops this key
   * on every write, so the next open re-reads it — the same shape
   * `DmReactionSummary` already had.
   */
  /**
   * WHO reacted is SIGNED-IN ONLY (OSO-695) — the counts on the pills are
   * not, so a signed-out reader on a public event's discussion still sees
   * them. For that reader the tooltip affordance is simply withheld: without
   * the gate, `enabled` flipped per hover and every mouse-enter re-fired a
   * 401 whose answer was already known.
   */
  const signedIn = useSignedIn();
  const { data } = usePostReactors(postId, visible && signedIn === true);
  const reactions = data?.items ?? null;

  // Every hook above must run unconditionally: bailing out before them makes
  // the hook count depend on `counts`, so a post going from zero reactions to
  // some (or back) throws "rendered more hooks than during the previous
  // render". Callers all guard on a hasReactions check today, which is the
  // only reason that isn't already happening.
  const entries = counts.filter((c) => c.count > 0);
  const total = entries.reduce((sum, c) => sum + c.count, 0);
  const topEntries = [...entries].sort((a, b) => b.count - a.count).slice(0, 3);

  if (entries.length === 0) return null;

  function handleMouseEnter() {
    // No tooltip signed out (or before `/me` has answered): the names are a
    // people-read and the request it would take is a known 401.
    if (!postId || signedIn !== true) return;
    setVisible(true);
  }

  function handleMouseLeave() {
    setVisible(false);
  }

  const tooltip = (() => {
    if (!visible || !postId) return null;
    if (reactions === null) return null;

    const { lines, overflow } = buildReactionTooltip(entries, reactions);

    return <ReactionTooltipCard anchorRef={anchorRef} lines={lines} overflow={overflow} />;
  })();

  return (
    <div
      ref={anchorRef}
      className="flex items-center gap-1.5"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {tooltip}
      <ReactionPillStack entries={topEntries} total={total} />
    </div>
  );
}
